import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { href: "/philosophy", label: "Philosophy" },
  { href: "/vehicles", label: "Vehicles" },
  { href: "/about", label: "About" },
  { href: "/blog", label: "Blog" },
  { href: "/contact", label: "Contact" },
];

export const Header = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#050505] border-b border-[#262626]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="font-display text-xl md:text-2xl tracking-tight"
            data-testid="nav-logo"
          >
            KELLER
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`font-mono text-xs uppercase tracking-wider transition-colors duration-200 ${
                  location.pathname === link.href 
                    ? "text-[#FF2A00]" 
                    : "text-[#A3A3A3] hover:text-white"
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/vehicles/adler">
              <button 
                className="font-mono text-xs uppercase tracking-wider bg-[#FF2A00] text-white px-6 py-3 hover:bg-white hover:text-black transition-colors duration-200"
                data-testid="nav-reserve-btn"
              >
                Reserve
              </button>
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <nav className="md:hidden py-6 border-t border-[#262626]">
            <div className="flex flex-col gap-4">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className={`font-mono text-sm uppercase tracking-wider ${
                    location.pathname === link.href 
                      ? "text-[#FF2A00]" 
                      : "text-[#A3A3A3]"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/vehicles/adler" onClick={() => setMobileOpen(false)}>
                <button className="w-full font-mono text-sm uppercase tracking-wider bg-[#FF2A00] text-white px-6 py-3 mt-2">
                  Reserve Now
                </button>
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-[#050505] border-t border-[#262626]">
      {/* Giant KELLER text */}
      <div className="overflow-hidden py-16 md:py-24">
        <p className="font-display text-[20vw] leading-none text-[#0F0F0F] text-center select-none">
          KELLER
        </p>
      </div>

      {/* Footer content */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-8 pb-12">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          {/* Brand */}
          <div>
            <p className="font-display text-2xl mb-4">KELLER MOTORS</p>
            <p className="text-sm text-[#A3A3A3] leading-relaxed">
              Leading the analog revolution from Vaihingen an der Enz, Germany. 
              Just miles from Porsche's Weissach and the birthplace of the internal combustion engine.
            </p>
          </div>

          {/* Links */}
          <div>
            <p className="font-mono text-xs uppercase tracking-wider text-[#A3A3A3] mb-4">Navigate</p>
            <div className="flex flex-col gap-2">
              <Link to="/philosophy" className="text-sm hover:text-[#FF2A00] transition-colors">Philosophy</Link>
              <Link to="/vehicles" className="text-sm hover:text-[#FF2A00] transition-colors">Vehicles</Link>
              <Link to="/about" className="text-sm hover:text-[#FF2A00] transition-colors">About</Link>
              <Link to="/contact" className="text-sm hover:text-[#FF2A00] transition-colors">Contact</Link>
            </div>
          </div>

          {/* Contact */}
          <div>
            <p className="font-mono text-xs uppercase tracking-wider text-[#A3A3A3] mb-4">Connect</p>
            <div className="flex flex-col gap-2 text-sm">
              <a href="mailto:taylor@keller-motors.com" className="hover:text-[#FF2A00] transition-colors">
                taylor@keller-motors.com
              </a>
              <a href="https://instagram.com/kellermotorsofficial" target="_blank" rel="noopener noreferrer" className="hover:text-[#FF2A00] transition-colors">
                Instagram @kellermotorsofficial
              </a>
              <a href="https://facebook.com/kellercars" target="_blank" rel="noopener noreferrer" className="hover:text-[#FF2A00] transition-colors">
                Facebook @kellercars
              </a>
              <a href="https://www.linkedin.com/company/keller-motors-inc./" target="_blank" rel="noopener noreferrer" className="hover:text-[#FF2A00] transition-colors">
                LinkedIn
              </a>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-[#262626] flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-center md:text-left">
            <p className="text-xs text-[#A3A3A3]">
              &copy; {new Date().getFullYear()} Keller Motors. The Analog Revolution.
            </p>
            <p className="text-xs text-[#A3A3A3] mt-1">
              * Final specifications and pricing subject to change within ±20% as development continues
            </p>
          </div>
          <p className="font-mono text-xs text-[#A3A3A3]">
            Vaihingen an der Enz, Germany
          </p>
        </div>
      </div>
    </footer>
  );
};
